import { createRouter, createWebHistory } from 'vue-router';
import MessageHub from '~/components/message-hub/MessageHub.vue';
import MessageHubBulkActions from '~/components/message-hub/message-hub-bulk-actions/MessageHubBulkActions.vue';
import MessageHubContactView from '~/components/message-hub/MessageHubContactView.vue';
import MessageHubConversation from '~/components/message-hub/MessageHubConversation.vue';
import MessageHubNewConversation from '~/components/message-hub/MessageHubNewConversation.vue';
import MessageHubScheduledMessages from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessages.vue';
import MessageHubScheduledMessage from '~/components/message-hub/message-hub-scheduled-messages/MessageHubScheduledMessage.vue';
import MessageHubNoThreadSelected from '~/components/message-hub/message-hub-threads/MessageHubNoThreadSelected.vue';
import { canUseMessageHub } from './components/message-hub/messageHubUtil';
import CampaignList from '~/components/campaigns/list/CampaignList.vue';
import CampaignStepForm from '~/components/campaigns/campaign-step-form/CampaignStepForm.vue';
import CampaignContacts from '~/components/campaigns/contacts/CampaignContacts.vue';
import CampaignDetails from '~/components/campaigns/CampaignDetails.vue';
import { useCurrentGroup } from './composables/useCurrentGroup';
import GroupQuickBlastEditForm from '~/components/quick-blast/edit/groups/GroupQuickBlastEditForm.vue';
import qs from 'qs';

const ReservedDatesList = () => import('~/components/reserved-dates/ReservedDatesList.vue');
const ReservedDateForm = () => import('~/components/reserved-dates/ReservedDateForm.vue');
const ReservedDateDetails = () => import('~/components/reserved-dates/ReservedDateDetails.vue');

const ContactList = () => import('~/components/contacts/contact/ContactList.vue');
const ContactForm = () => import('~/components/contacts/contact/form/ContactForm.vue');
const ContactDetails = () => import('~/components/contacts/contact/ContactDetails.vue');
const ContactView = () => import('~/components/contacts/contact/ContactView.vue');
const ContactMessages = () => import('~/components/contacts/contact/messages/ContactMessages.vue');
const ContactSubscriptions = () => import('~/components/contacts/contact/subscriptions/ContactSubscriptions.vue');
const ContactCustomAttributes = () =>
    import('~/components/contacts/contact/custom-attributes/ContactCustomAttributes.vue');
const ContactCampaigns = () => import('~/components/contacts/contact/campaigns/ContactCampaigns.vue');
const ContactPromotions = () => import('~/components/contacts/contact/promotions/ContactPromotions.vue');
const ContactSegments = () => import('~/components/contacts/contact/segments/ContactSegments.vue');

const AutomationRuleList = () => import('~/components/automation/AutomationRuleList.vue');
const AutomationRuleForm = () => import('~/components/automation/AutomationRuleForm.vue');

const FlowsList = () => import('~/components/automation/flows/FlowsList.vue');
const FlowForm = () => import('~/components/automation/flows/FlowForm.vue');
const FlowDetailForm = () => import('~/components/automation/flows/flow/FlowDetailForm.vue');
const FlowResources = () => import('~/components/automation/flows/flow/FlowResources.vue');

const WorkflowBuilderLayout = () => import('./components/workflow-builder/WorkflowBuilderLayout.vue');
const WorkflowBuilder = () => import('./components/workflow-builder/pages/WorkflowBuilder.vue');
const WorkflowBuilderHistory = () => import('./components/workflow-builder/pages/WorkflowBuilderHistory.vue');
const WorkflowBuilderArchived = () => import('./components/workflow-builder/pages/WorkflowBuilderArchived.vue');

const { onCorporate } = useCurrentGroup();
import { FEATURES } from '~/provides/features';

const paths = [
    {
        path: '/workflow-builder',
        component: WorkflowBuilderLayout,
        meta: { title: 'Workflow Builder' },
        beforeEnter: () => {
            if (!FEATURES.workflow_builder || !onCorporate()) {
                window.location.assign('/');
                return false;
            }

            return true;
        },
        children: [
            {
                path: '',
                name: 'workflow-builder',
                meta: { noContainerSpacing: true },
                component: WorkflowBuilder,
            },
            {
                path: 'history',
                name: 'workflow-builder.history',
                meta: { title: 'History · Workflow Builder' },
                component: WorkflowBuilderHistory,
            },
            {
                path: 'archived',
                name: 'workflow-builder.archived',
                meta: { title: 'Archived · Workflow Builder' },
                component: WorkflowBuilderArchived,
            },
        ],
    },
    {
        path: '/message-hub',
        component: MessageHub,
        beforeEnter: () => {
            if (!canUseMessageHub()) {
                window.location.assign('/');
                return false;
            }

            return true;
        },
        children: [
            {
                path: '',
                name: 'message-hub',
                component: MessageHubNoThreadSelected,
            },
            {
                path: 'new-message',
                name: 'message-hub.new-message',
                component: MessageHubNewConversation,
            },
            {
                path: 'bulk-actions',
                name: 'message-hub.bulk-actions',
                component: MessageHubBulkActions,
            },
            {
                path: 'scheduled',
                name: 'message-hub.scheduled-messages',
                component: MessageHubScheduledMessages,
            },
            {
                path: 'scheduled/:scheduledMessageId',
                name: 'message-hub.scheduled-message',
                component: MessageHubScheduledMessage,
            },
            {
                path: ':contactId',
                name: 'message-hub.conversation',
                component: MessageHubConversation,
                children: [
                    {
                        path: 'view',
                        name: 'message-hub.contact-view',
                        component: MessageHubContactView,
                    },
                ],
            },
            // Add 404 catch-all route
            {
                path: ':pathMatch(.*)*',
                name: 'not-found',
                component: MessageHubConversation,
            },
        ],
    },
    {
        path: '/messaging',
        children: [
            {
                path: 'quick-blast',
                children: [
                    {
                        path: 'new',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.new',
                        meta: { title: 'Create Quick Blast' },
                        beforeEnter: (to) => {
                            if (!to.query.duplicate_from_id || onCorporate()) {
                                window.location.assign('/messaging/quick-blast');
                                return false;
                            }

                            return true;
                        },
                    },
                    {
                        path: ':id(\\d+)',
                        component: GroupQuickBlastEditForm,
                        name: 'quick-blast.edit',
                        meta: { title: 'Campaign Details' },
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return { name: 'campaigns' };
                            }
                        },
                    },
                ],
            },
            {
                path: 'campaigns',
                children: [
                    {
                        path: '',
                        name: 'campaigns',
                        component: CampaignList,
                        meta: { title: 'Campaigns' },
                    },
                    {
                        path: 'new',
                        component: CampaignDetails,
                        beforeEnter: (to) => {
                            if (onCorporate()) {
                                return;
                            }

                            if (to.query.duplicate_from_id) {
                                return {
                                    name: 'quick-blast.new',
                                    query: { duplicate_from_id: to.query.duplicate_from_id },
                                };
                            }

                            window.location.assign('/messaging/quick-blast');
                            return false;
                        },
                        children: [
                            {
                                path: '',
                                name: 'campaigns.new',
                                component: CampaignStepForm,
                                meta: { title: 'Create Campaign' },
                            },
                        ],
                    },
                    {
                        path: ':id(\\d+)',
                        component: CampaignDetails,
                        props: (route) => ({ campaignId: Number(route.params.id) }),
                        children: [
                            {
                                path: '',
                                name: 'campaigns.edit',
                                component: CampaignStepForm,
                                meta: { title: 'Campaign Details' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: (to) => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'quick-blast.edit', params: to.params, query: to.query };
                                },
                            },
                            {
                                path: 'contacts',
                                name: 'campaigns.contacts',
                                component: CampaignContacts,
                                meta: { title: 'Campaign Contacts' },
                                props: (route) => ({ campaignId: Number(route.params.id) }),
                                beforeEnter: () => {
                                    if (onCorporate()) {
                                        return;
                                    }

                                    return { name: 'campaigns' };
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'reserved-dates',
                beforeEnter: () => {
                    if (FEATURES.contact_groups && onCorporate()) {
                        return;
                    }

                    window.location.assign('/');
                    return false;
                },
                children: [
                    {
                        path: '',
                        name: 'reserved-dates',
                        component: ReservedDatesList,
                        meta: { title: 'Reserved Dates' },
                    },
                    {
                        path: 'new',
                        component: ReservedDateDetails,
                        children: [
                            {
                                path: '',
                                name: 'reserved-dates.new',
                                component: ReservedDateForm,
                                meta: { title: 'New Reserved Date' },
                            },
                        ],
                    },
                    {
                        path: ':id(\\d+)',
                        component: ReservedDateDetails,
                        props: (route) => ({ reservedDateId: Number(route.params.id) }),
                        children: [
                            {
                                path: '',
                                name: 'reserved-dates.edit',
                                component: ReservedDateForm,
                                meta: { title: 'Edit Reserved Date' },
                                props: (route) => ({ reservedDateId: Number(route.params.id) }),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '/contacts',
        children: [
            {
                path: '',
                name: 'contacts.list',
                component: ContactList,
                meta: { title: 'Contacts' },
            },
            {
                path: 'new',
                component: ContactDetails,
                children: [
                    {
                        path: '',
                        name: 'contacts.new',
                        component: ContactForm,
                        meta: { title: 'New Contact' },
                    },
                ],
            },
            {
                path: ':contactId(\\d+)',
                component: ContactDetails,
                props: (route) => ({ contactId: Number(route.params.contactId) }),
                children: [
                    {
                        path: '',
                        name: 'contacts.edit',
                        component: ContactForm,
                        meta: { title: 'Edit Contact' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'view',
                        name: 'contacts.view',
                        component: ContactView,
                        meta: { title: 'Contact' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'messages',
                        name: 'contacts.messages',
                        component: ContactMessages,
                        meta: { title: 'Messages' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'subscriptions',
                        name: 'contacts.subscriptions',
                        component: ContactSubscriptions,
                        meta: { title: 'Subscriptions' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'custom-attributes',
                        name: 'contacts.custom-attributes',
                        component: ContactCustomAttributes,
                        meta: { title: 'Custom Attributes' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                    },
                    {
                        path: 'campaigns',
                        name: 'contacts.campaigns',
                        component: ContactCampaigns,
                        meta: { title: 'Campaigns' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                    {
                        path: 'promotions',
                        name: 'contacts.promotions',
                        component: ContactPromotions,
                        meta: { title: 'Promotions' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                    {
                        path: 'segments',
                        name: 'contacts.segments',
                        component: ContactSegments,
                        meta: { title: 'Segments' },
                        props: (route) => ({ contactId: Number(route.params.contactId) }),
                        beforeEnter: () => {
                            if (onCorporate()) {
                                return;
                            }
                            return { name: 'contacts.list' };
                        },
                    },
                ],
            },
        ],
    },
    {
        path: '/automation/event',
        children: [
            {
                path: '',
                name: 'automation.list',
                component: AutomationRuleList,
                meta: { title: 'Automations' },
            },
            {
                path: 'new',
                name: 'automation.new',
                component: AutomationRuleForm,
                meta: { title: 'Create Automation' },
            },
            {
                path: ':automationRuleId(\\d+)',
                name: 'automation.edit',
                component: AutomationRuleForm,
                meta: { title: 'Edit Automation' },
                props: (route) => ({ automationRuleId: Number(route.params.automationRuleId) }),
            },
        ],
    },
    {
        path: '/automation/flows',
        beforeEnter: () => {
            if (FEATURES.flows && onCorporate()) {
                return;
            }
            return { name: 'automation.list' };
        },
        children: [
            {
                path: '',
                name: 'flows.list',
                component: FlowsList,
                meta: { title: 'Flows' },
            },
            {
                path: 'new',
                component: FlowForm,
                children: [
                    {
                        path: '',
                        name: 'flows.new',
                        component: FlowDetailForm,
                        meta: { title: 'Create Flow' },
                    },
                ],
            },
            {
                path: ':flowId',
                component: FlowForm,
                props: (route) => ({ flowId: route.params.flowId }),
                children: [
                    {
                        path: '',
                        name: 'flows.edit',
                        component: FlowDetailForm,
                        meta: { title: 'Edit Flow' },
                    },
                    {
                        path: 'resources',
                        name: 'flows.resources',
                        component: FlowResources,
                        meta: { title: 'Flow Resources' },
                        props: (route) => ({ flowId: route.params.flowId }),
                    },
                ],
            },
        ],
    },
];

export const routes = createRouter({
    history: createWebHistory(),
    routes: paths,
    scrollBehavior(to, from) {
        if (to.hash) {
            return { el: to.hash, behavior: 'smooth' };
        }
        if (to.params.contactId && to.params.contactId === from.params.contactId) {
            return;
        }
        if (to.path !== from.path) {
            return { left: 0, top: 0 };
        }
    },
    stringifyQuery: (query) => {
        return qs.stringify(query, { arrayFormat: 'brackets', skipNulls: true });
    },
    parseQuery: (query) => {
        return qs.parse(query, { arrayFormat: 'brackets', skipNulls: true, ignoreQueryPrefix: true });
    },
});

routes.afterEach((to) => {
    if (to.meta?.title) {
        document.title = `${to.meta.title} · Voxie`;
    }
});
